import type { ConditionState } from '@/stores/segmentBuilder.store';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';
import { CountOperator, TimeBoundary } from '@/gql/graphql';
import { getConditionDefinition, InclusionOperator } from '../config/conditionRegistry';
import { TimeBoundarySelector } from './TimeBoundarySelector';
import { StyledSelectTrigger } from './StyledSelectTrigger';

interface ConditionRowProps {
  config: ConditionState;
  onUpdate: (newConfig: ConditionState) => void;
  onDelete: () => void;
}

export function ConditionRow({ config, onUpdate, onDelete }: ConditionRowProps) {
  const definition = getConditionDefinition(config.eventName);

  const handleInclusionChange = (value: string) => {
    const op = value as InclusionOperator;

    if (
      definition &&
      definition.type === 'event' &&
      (definition.availableOperators as InclusionOperator[]).includes(op)
    ) {
      const newOperator = op === 'has' ? CountOperator.GreaterThanOrEqualTo : CountOperator.EqualTo;
      const newNumTimes = op === 'has' ? 1 : 0;
      onUpdate({ ...config, operator: newOperator, numTimes: newNumTimes });
    } else {
      console.warn(
        'Selected operator not valid for this event condition definition',
        op,
        definition
      );
    }
    // TODO: Add handling for user property ComparisonOperators later
  };

  // Handler for updates coming from TimeBoundarySelector
  const handleTimeBoundaryChange = (newTimeBoundary: TimeBoundary | null) => {
    onUpdate({ ...config, timeBoundary: newTimeBoundary ?? null });
  };

  const inclusionValue =
    config.operator === CountOperator.GreaterThanOrEqualTo && config.numTimes >= 1
      ? 'has'
      : config.operator === CountOperator.EqualTo && config.numTimes === 0
        ? 'has_not'
        : 'has';

  if (!definition) {
    return (
      <div className="flex items-center space-x-2 p-3 border rounded bg-destructive/10 text-destructive">
        <span>Unsupported condition type: {config.eventName}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={onDelete}>
          <X />
        </Button>
      </div>
    );
  }

  // Dynamically build the sentence UI based on definition.sentenceStructure
  const renderSentencePart = (part: string, index: number) => {
    switch (part) {
      case '[Operator]':
        return (
          <Select key={index} value={inclusionValue} onValueChange={handleInclusionChange}>
            <StyledSelectTrigger>
              <SelectValue />
            </StyledSelectTrigger>
            <SelectContent>
              {definition.availableOperators.map((op) => (
                <SelectItem key={op} value={op}>
                  {op}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case '[EventName]':
        return (
          <span key={index} className="text-sm text-card-foreground">
            {definition.label}
          </span>
        );
      case '[TimeFrame]':
        return (
          <TimeBoundarySelector
            key={index}
            value={config.timeBoundary}
            onChange={handleTimeBoundaryChange}
          />
        );
      case '[PropertyFilters]':
        return null; // Or <PropertyFilterEditor config={...} /> later
      default:
        return (
          <span key={index} className="text-sm text-card-foreground">
            {part}
          </span>
        );
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 rounded-lg border border-base-border bg-card">
      {definition.sentenceStructure.map(renderSentencePart)}
      {/* Delete Button always at the end */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-destructive/90 ml-auto"
        onClick={onDelete}
      >
        <span className="sr-only">Delete condition</span>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
